import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { auth, firestore } from './firebase';
import './Profile.css'; // Make sure to create this CSS file
import '@fortawesome/fontawesome-free/css/all.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [patientFiles, setPatientFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [loadedOwner, setLoadedOwner] = useState(null);
  const [editText, setEditText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [loadError, setLoadError] = useState('');
  const lifecycleRef = useRef({ mounted: false, epoch: 0, ownerUid: null });
  const selectionRef = useRef({ id: null, revision: 0 });
  const activeSaveRef = useRef(null);
  const saveSequenceRef = useRef(0);
  const selectedFile = patientFiles.find(file => file.id === selectedFileId) || null;

  const isCurrentSession = useCallback(owner => (
    Boolean(owner) && lifecycleRef.current.mounted &&
    lifecycleRef.current.epoch === owner.epoch &&
    lifecycleRef.current.ownerUid === owner.uid &&
    auth.currentUser?.uid === owner.uid
  ), []);

  useEffect(() => {
    let subscribed = true;
    const lifecycle = lifecycleRef.current;
    lifecycle.mounted = true;
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (!subscribed) return;
      const epoch = ++lifecycle.epoch;
      const isCurrent = () => subscribed && lifecycle.mounted &&
        epoch === lifecycle.epoch && auth.currentUser?.uid === user?.uid;
      lifecycle.ownerUid = null;
      activeSaveRef.current = null;
      selectionRef.current = { id: null, revision: selectionRef.current.revision + 1 };
      setLoadedOwner(null);
      setUserData(null);
      setPatientFiles([]);
      setSelectedFileId(null);
      setEditText('');
      setIsSaving(false);
      setSaveError('');
      setIsEditingName(false);
      setLoadError('');
      setAuthInitialized(true);

      if (!user) {
        setLoadError('Please sign in to view your profile.');
        return;
      }

      try {
        const userDocSnap = await getDoc(doc(firestore, 'users', user.uid));
        if (!isCurrent()) return;
        if (!userDocSnap.exists()) {
          setLoadError('Your profile could not be found.');
          return;
        }

        const querySnapshot = await getDocs(collection(firestore, 'users', user.uid, 'files'));
        if (!isCurrent()) return;
        const files = querySnapshot.docs.map(file => {
          const data = file.data();
          return {
            id: file.id,
            name: data.name,
            text: data.text ? atob(data.text) : '',
            diagnoses: data.diagnoses ? atob(data.diagnoses) : '',
            insights: data.insights ? atob(data.insights) : '',
          };
        });
        lifecycle.ownerUid = user.uid;
        setLoadedOwner({ uid: user.uid, epoch });
        setUserData(userDocSnap.data());
        setPatientFiles(files);
      } catch {
        if (isCurrent()) setLoadError('Unable to load your profile. Please try again.');
      }
    });

    return () => {
      subscribed = false;
      lifecycle.mounted = false;
      lifecycle.epoch += 1;
      lifecycle.ownerUid = null;
      activeSaveRef.current = null;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!selectedFileId || !isCurrentSession(loadedOwner)) return;
    const { uid } = loadedOwner;
    const updateAccessedBy = async () => {
      try {
        const fileRef = doc(firestore, 'users', uid, 'files', selectedFileId);
        await updateDoc(fileRef, { accessedBy: uid });
        if (isCurrentSession(loadedOwner)) console.log('File access updated successfully');
      } catch (error) {
        if (isCurrentSession(loadedOwner)) console.error('Error updating file access: ', error);
      }
    };
    updateAccessedBy();
  }, [selectedFileId, loadedOwner, isCurrentSession]);

  const handleSelectFile = file => {
    if (!isCurrentSession(loadedOwner) || selectionRef.current.id === file.id) return;
    selectionRef.current = { id: file.id, revision: selectionRef.current.revision + 1 };
    setSelectedFileId(file.id);
    // Files stay decoded in state. Selecting a file starts its own editor view.
    setEditText(file.text);
    setSaveError('');
  };

  const getSourceUrl = (filename) => {
    let displayText = filename.split('/').pop().split('.')[0].replace(/-/g, ' ');
    if (displayText.includes('pdf')) {
      displayText = displayText.split('pdf')[0];
    }
    displayText = displayText.charAt(0).toUpperCase() + displayText.slice(1);
    let iconUrl = '';

    if (filename.startsWith('medsafePDFs')) {
      iconUrl = 'https://firebasestorage.googleapis.com/v0/b/resurg-62b32.appspot.com/o/medsafe.png?alt=media&token=08db8ee6-55d4-46e9-ba08-61850a488479';
      const drugName = filename.split('/')[1].split('.')[0];
      return {
        url: `https://www.medsafe.govt.nz/profs/datasheet/${drugName.charAt(0)}/${filename.split('/')[1]}`,
        text: displayText,
        icon: iconUrl
      };
    } else if (filename.startsWith('nicePDFs')) {
      iconUrl = 'https://firebasestorage.googleapis.com/v0/b/resurg-62b32.appspot.com/o/nice.png?alt=media&token=0e7437c3-1b2a-4f5b-84e3-f62a5c18127f';
      const searchTerm = filename.split('-').slice(3).join(' ');
      return {
        url: `https://www.nice.org.uk/search?q=${encodeURIComponent(searchTerm)}`,
        text: displayText,
        icon: iconUrl
      };
    } else if (filename.startsWith('https://bpac.org.nz')) {
      iconUrl = 'https://firebasestorage.googleapis.com/v0/b/resurg-62b32.appspot.com/o/bpac3.png?alt=media&token=e002c6c4-f5b5-4f15-9b58-24b8bec9d25d';
      displayText = filename.split('/')[filename.split('/').length - 1].split('.')[0];
      return {
        url: filename,
        text: displayText,
        icon: iconUrl
      };
    } else {
      // Handle other cases or add a default icon if needed
      iconUrl = 'https://firebasestorage.googleapis.com/v0/b/resurg-62b32.appspot.com/o/pubmed.png?alt=media&token=97def8cc-73d8-4d08-873b-a810be4d9a82';
      const searchTerm = filename.replace(/\s/g, '+'); // Replace all spaces with '+'
      return {
        url: `https://pubmed.ncbi.nlm.nih.gov/?term=${searchTerm}`,
        text: filename,
        icon: iconUrl // Default icon URL or leave as an empty string
      };
    }
  };

  const exportPDF = () => {
    const input = document.getElementById('pdf-content');
    input.style.display = 'block'; // Temporarily display the div

    // Get current date and time
    const now = new Date();
    const dateOptions = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    const formattedDate = now.toLocaleDateString('en-GB', dateOptions);

    // Add date to the div
    const dateElement = document.createElement('p');
    dateElement.textContent = formattedDate;
    input.appendChild(dateElement);

    html2canvas(input)
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', [canvas.width * 0.2645833333, canvas.height * 0.2645833333]);
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width * 0.2645833333, canvas.height * 0.2645833333);
        pdf.save(`${selectedFile.name.split('-')[0]}.pdf`);
      })
      .finally(() => {
        input.style.display = 'none'; // Hide the div again after PDF generation
        // Remove the date element
        input.removeChild(dateElement);
      });
  };

  const handleNameBlur = async () => {
    setIsEditingName(false);
    if (!isCurrentSession(loadedOwner) || !userData) return;
    const owner = loadedOwner;
    const name = userData.name;
    try {
      const userRef = doc(firestore, 'users', owner.uid);
      await updateDoc(userRef, { name });
    } catch (error) {
      if (isCurrentSession(owner)) console.error('Error updating name: ', error);
    }
  };

  const renderTextWithSpacingBeforeNumbers = (text) => {
    // Insert a unique marker before digits followed by a period to denote new sections.
    // The marker will help us to split the text and add space before the number.
    const updatedText = text.replace(/(\d+\.\s?)/g, '|||$1');

    // Split the text at each marker, creating an array where each element is either a section of text or a numbered item.
    const parts = updatedText.split('|||');

    // Filter out empty strings that may result from the split to avoid rendering empty elements.
    const nonEmptyParts = parts.filter(part => part.trim() !== '');

    // Map over the non-empty parts, rendering each part as a separate element.
    // Add top margin to create visual separation, but only to elements starting with a number,
    // which indicates a new section according to the original pattern.
    return nonEmptyParts.map((part, index) => (
      <div key={index} style={{ marginTop: part.match(/^\d+\.\s?/) ? '1rem' : '0' }}>
        {part}
      </div>
    ));
  };

  const handleSave = async () => {
    if (!selectedFile || !isCurrentSession(loadedOwner) || activeSaveRef.current ||
      selectionRef.current.id !== selectedFile.id) return;

    const operation = {
      id: ++saveSequenceRef.current,
      owner: loadedOwner,
      fileId: selectedFile.id,
      text: editText,
      selectionRevision: selectionRef.current.revision,
    };
    activeSaveRef.current = operation;
    const isCurrent = () => isCurrentSession(operation.owner) && activeSaveRef.current === operation;
    setIsSaving(true);
    setSaveError('');
    try {
      const encodedText = btoa(operation.text);
      const fileRef = doc(firestore, 'users', operation.owner.uid, 'files', operation.fileId);
      await updateDoc(fileRef, { text: encodedText });
      if (!isCurrent()) return;

      setPatientFiles(files => isCurrentSession(operation.owner)
        ? files.map(file => file.id === operation.fileId ? { ...file, text: operation.text } : file)
        : files);
      // The global save lock prevents editing during a pending save, including
      // after selecting another patient and returning to this one.
      setEditText(text => isCurrentSession(operation.owner) &&
        saveSequenceRef.current === operation.id && selectionRef.current.id === operation.fileId
        ? operation.text : text);
    } catch (error) {
      if (isCurrent()) {
        console.error('Error updating file: ', error);
        if (selectionRef.current.id === operation.fileId &&
          selectionRef.current.revision === operation.selectionRevision) {
          setSaveError('Failed to save changes. Please try again.');
        }
      }
    } finally {
      // Release the session's lock even when a different patient is selected.
      if (isCurrent()) {
        activeSaveRef.current = null;
        setIsSaving(false);
      }
    }
  };

  if (!authInitialized) {
    return <div>Loading medr Scribe...</div>; // Show loading state until auth is initialized
  }

  if (loadError) {
    return <div role="alert">{loadError}</div>;
  }

  if (!userData) {
    return <div>Loading patient list...</div>; // Show loading state until user data is fetched
  }

  return (
    <div className="profile-container">
      <aside className="sidebar container1">
        <img src="https://firebasestorage.googleapis.com/v0/b/resurg-62b32.appspot.com/o/logo_blue.png?alt=media&token=a00fbd88-3708-45ad-b037-df88343b53bf" alt="Logo" style={{ width: '170px', height: '120px' }} />
        <h3>Patients</h3>
        <ul>
          {patientFiles.map(file => (
            <li key={file.id} onClick={() => handleSelectFile(file)}>
              {file.name.split('-')[0]}
            </li>
          ))}
        </ul>
      </aside>
      <main className="content">
        <div className="user-info">
          {isEditingName ? (
            <input
              value={userData.name}
              onChange={(e) => setUserData({ ...userData, name: e.target.value })}
              onBlur={handleNameBlur}
            />
          ) : (
            <h1>{userData.name}</h1>
          )}
          <i className="fas fa-pencil-alt" onClick={() => setIsEditingName(true)}></i>
          <p className="specialty">{userData.specialty}</p>
        </div>
        {selectedFile && (
          <div className="patient-details-container">
            <div className="patient-details container">
              <h2>
                {selectedFile.name.split('-')[0]}
                <FontAwesomeIcon icon={faFilePdf} onClick={exportPDF} style={{ marginLeft: '20px', cursor: 'pointer', color: 'purple' }} />
              </h2>
              <textarea
                className="edit-textarea"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                disabled={isSaving}
              />
              <button className="save-btn" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              {saveError && <p className="error">{saveError}</p>}
            </div>
            <div id="pdf-content" style={{ display: 'none', whiteSpace: 'pre-wrap', padding: '20px 60px' }}>
              <h2>{selectedFile.name.split('-')[0]}</h2>
              <p style={{ textAlign: 'left' }}>{editText}</p>
              <br />
              <br />
              <strong>
                <p>{userData.name}</p>
              </strong>
              <p>{userData.specialty}</p>
            </div>
            <div className="patient-insights">
              <div className="container2 scrollable-container1">
                <h3 className="title">Differential Dx</h3>
                {renderTextWithSpacingBeforeNumbers(selectedFile.diagnoses)}
              </div>
              <div className="container3 scrollable-container">
                <h3 className="title">Insights</h3>
                <div>
                  {selectedFile.insights.includes('Sources:') && (
                    <p>{renderTextWithSpacingBeforeNumbers(selectedFile.insights.split('Sources:')[0])}</p>
                  )}
                  <p> </p>
                  <div>
                    {selectedFile.insights.split('Sources:')[1]?.split('\n').map((line, index) => {
                      line = line.trim(); // Trim whitespace from the line
                      if (line === '') {
                        return null; // If the line is empty, don't render anything
                      }
                      const { url, text, icon } = getSourceUrl(line);
                      return (
                        <p key={index}>
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            {icon && <img src={icon} alt="Source icon" style={{ width: '16px', verticalAlign: 'middle', marginRight: '5px' }} />}
                            {text}
                          </a>
                        </p>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Profile;
